<template>
  <div class="google-sign-in-button-component" :id="googleSignInButtonId"></div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useAuthStore } from '@tallo/auth';
import { handleError } from '@tallo/design-system';
import { useNavigation } from '@tallo/investor/navigation';

const props = defineProps<{ flow: 'sign-up' | 'sign-in' }>();
const router = useRouter();
const authStore = useAuthStore();

const { openPortfolio, openOnboarding } = useNavigation();

const googleSignInButtonId = ref('tallo-investor-google-sign-in-button');

async function googleSignInCallback(googleJwt: string) {
  try {
    const { companyId } = await signInOrSignUp(googleJwt);

    // A 2FA-gated sign-in is caught by the router guard and sent to the code screen instead.
    navigateAfterAccountVerified(companyId);
  } catch (error) {
    handleError(error);
  }
}

function navigateAfterAccountVerified(companyId: string | null) {
  const redirect = props.flow === 'sign-in' ? authStore.consumePostSignInRedirect() : null;

  if (redirect) {
    return router.push(redirect);
  }

  if (companyId) {
    openOnboarding();
  } else {
    openPortfolio();
  }
}

async function signInOrSignUp(googleJwt: string): Promise<{ companyId: string | null; twoFactorRequired: boolean }> {
  if (props.flow === 'sign-up') {
    return authStore.investorSignUpWithGoogle(googleJwt);
  } else {
    return authStore.investorSignInWithGoogle(googleJwt);
  }
}

onMounted(() => {
  authStore.renderGoogleSignInButton({
    containerHtmlElementId: googleSignInButtonId.value,
    text: props.flow === 'sign-up' ? 'signup_with' : 'continue_with',
    callback: googleSignInCallback,
  });
});
</script>

<style scoped lang="scss">
.google-sign-in-button-component {
  width: 100%;
  height: 2.75rem;
}
</style>
